/**
 * /editorial-standards route — GNPC CRITICAL transparency page.
 *
 * Spec: GTM/GNPC-APPLICATION-KIT.md §3.3 + S3 of dispatch.
 * Required by Google News Publisher Center; also required by AI Law 2026 Art.28
 * for AI system transparency disclosure.
 */

import { useEffect, useMemo } from 'react';
import { useLang } from '../use-lang';
import { resolveEditorial } from '../i18n/editorial-standards';

export function EditorialStandardsRoute() {
  const { lang } = useLang();
  const copy = useMemo(() => resolveEditorial(lang), [lang]);

  useEffect(() => {
    document.title = copy.meta_title;
    setMeta('description', copy.meta_description);
    setMeta('og:title', copy.meta_title, true);
    setMeta('og:description', copy.meta_description, true);
  }, [copy]);

  return (
    <main style={{ maxWidth: 760, margin: '32px auto 96px', padding: '0 16px' }}>
      <h1 className="h-display" style={{ fontSize: 'clamp(28px, 4vw, 40px)', marginTop: 0 }}>
        {copy.page_title}
      </h1>

      <p className="body-lead" style={{ marginBottom: 32 }}>
        {copy.preamble}
      </p>

      <Section heading={copy.s1_heading}>
        <p>{copy.s1_body}</p>
      </Section>

      <Section heading={copy.s2_heading}>
        <p>{copy.s2_body}</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th scope="col" style={th}>{copy.s2_table_source}</th>
                <th scope="col" style={th}>{copy.s2_table_type}</th>
                <th scope="col" style={th}>{copy.s2_table_trust}</th>
              </tr>
            </thead>
            <tbody>
              {copy.s2_rows.map((r, i) => (
                <tr key={i}>
                  <td style={td}>{r.source}</td>
                  <td style={td}>{r.type}</td>
                  <td style={td}>{r.trust}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ marginTop: 16, fontSize: 14, color: 'var(--color-muted)' }}>
          {copy.s2_phase2}
        </p>
      </Section>

      <Section heading={copy.s3_heading}>
        <ul>
          <li><strong>{copy.s3_model}</strong></li>
          <li>{copy.s3_length}</li>
          <li>{copy.s3_tone}</li>
          <li>{copy.s3_forbidden}</li>
          <li>{copy.s3_case_convention}</li>
        </ul>
      </Section>

      <Section heading={copy.s4_heading}>
        <p>{copy.s4_methodology_intro}</p>
        <ol>
          <li>{copy.s4_step_embedding}</li>
          <li>{copy.s4_step_search}</li>
          <li>{copy.s4_step_rerank}</li>
          <li>{copy.s4_step_threshold}</li>
        </ol>
        <p>{copy.s4_only_corpus}</p>
      </Section>

      <Section heading={copy.s5_heading}>
        <ul>
          <li>{copy.s5_nli}</li>
          <li>{copy.s5_status}</li>
          <li>{copy.s5_spam}</li>
          <li>{copy.s5_bias}</li>
          <li>{copy.s5_forbidden}</li>
        </ul>
      </Section>

      <Section heading={copy.s6_heading}>
        <ul>
          <li>{copy.s6_feedback}</li>
          <li>{copy.s6_correction}</li>
          <li>{copy.s6_takedown}</li>
        </ul>
      </Section>

      <Section heading={copy.s7_heading}>
        <ul>
          <li>{copy.s7_byline}</li>
          <li>{copy.s7_automation}</li>
          <li>{copy.s7_human_review}</li>
          <li>{copy.s7_no_lawyer}</li>
        </ul>
      </Section>

      <Section heading={copy.s8_heading}>
        <ul>
          <li>{copy.s8_no_ads}</li>
          <li>{copy.s8_no_political}</li>
          <li>{copy.s8_no_lawyer_sponsor}</li>
          <li>{copy.s8_no_representation}</li>
          <li>{copy.s8_business_model}</li>
        </ul>
      </Section>

      <Section heading={copy.s9_heading}>
        <ul>
          <li>{copy.s9_upl}</li>
          <li>{copy.s9_ai}</li>
          <li>{copy.s9_pdpa}</li>
          <li>{copy.s9_ip}</li>
          <li>{copy.s9_press}</li>
          <li>{copy.s9_cyber}</li>
        </ul>
      </Section>

      <Section heading={copy.s10_heading}>
        <ul>
          <li>
            {copy.s10_feedback.split('bug@asklaw.vn')[0]}
            <a href="mailto:bug@asklaw.vn">bug@asklaw.vn</a>
          </li>
          <li>
            {copy.s10_takedown.split('legal@asklaw.vn')[0]}
            <a href="mailto:legal@asklaw.vn">legal@asklaw.vn</a>
            {copy.s10_takedown.split('legal@asklaw.vn')[1]}
          </li>
          <li>
            {copy.s10_dpo.split('dpo@asklaw.vn')[0]}
            <a href="mailto:dpo@asklaw.vn">dpo@asklaw.vn</a>
          </li>
          <li>{copy.s10_updated}</li>
        </ul>
      </Section>

      <hr style={{ marginTop: 48, marginBottom: 24, border: 0, borderTop: '1px solid var(--color-border)' }} />

      <p style={{ fontSize: 13, color: 'var(--color-muted)', fontStyle: 'italic' }}>
        {copy.living_doc_note}
      </p>
    </main>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  const id = headingToId(heading);
  return (
    <section aria-labelledby={id} style={{ marginTop: 32 }}>
      <h2 id={id} className="h-section" style={{ fontSize: 22 }}>
        {heading}
      </h2>
      {children}
    </section>
  );
}

function headingToId(s: string): string {
  return 'editorial-' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '2px solid var(--color-border)',
  fontWeight: 600,
};

const td: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border)',
  verticalAlign: 'top',
};

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}
